import { TimelineEntryKind } from "types/graphql-schema";

import { ComplianceProgramTimelineDays } from "@apps/programs/components/compliance-program-timeline";
import { conditionalTimelineMessage } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import Loading from "@components/loading/loading";

import ComplianceProgramFormReminders from "../compliance-program-form-reminders";
import { ProgramForm } from "../types";
import useProgramFormTimeline, {
  savedReviewMessage,
  timelineGapMessage,
} from "../use-program-form-timeline";

/**
 * The Notifications tab: what the review sends on its own, then what the admin
 * adds to it.
 *
 * The tab used to hold the reminder table alone. An admin opening it to ask "which
 * notifications will this send, and when?" found an empty reminder list and
 * concluded the review sends nothing, when in fact it sends up to nine messages
 * nobody schedules. The answer above the reminders is the Timeline's own
 * notification rows, so it can never fall out of step with what is really sent:
 * there is no second list of notifications written down anywhere.
 *
 * Opening the tab is the question, so nothing here waits to be asked a second
 * time: a tab that answers with a button to press first is the empty tab again.
 */
const ProgramFormNotifications = ({
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  canUpdate,
}: {
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: Partial<ProgramForm>) => void;
  canUpdate: boolean;
}) => {
  const { noun } = useProgramsSection();
  const {
    rows,
    loading,
    error,
    refusal,
    gap,
    showsSavedReview,
    conditionalKickoffAfterDays,
  } = useProgramFormTimeline(proposedComplianceProgram);

  // Only the automatic messages, and only while they describe the form as it is
  // now. The admin's own reminders are rows of the plan too, but they are edited
  // in the table below and listing them twice on one screen reads as two
  // reminders.
  const automatic = (gap === null ? (rows ?? []) : []).filter(
    (row) => row.kind === TimelineEntryKind.Notification,
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="text-xl font-semibold">
          {`What this ${noun.toLowerCase()} sends on its own`}
        </div>
        {gap === null && loading && rows && <Loading size="4" mini />}
      </div>
      <div className="mt-1 text-sm text-gray-500">
        These go out on their own as each step opens and falls due. You do not
        schedule them, and they are not in the table below.
      </div>

      {/* A running review is read off itself, so an unsaved edit is not in the
          list below. Said here because the list looks the same either way. */}
      {showsSavedReview && (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {savedReviewMessage(noun.toLowerCase())}
        </div>
      )}

      {/* A conditional review has no dates of its own, so the list below is dated
          from today. The same two sentences as the Workflow tab's panel, because
          the two are filtered views of one plan. */}
      {conditionalKickoffAfterDays !== null && (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {conditionalTimelineMessage(
            noun.toLowerCase(),
            conditionalKickoffAfterDays,
          ).map((sentence) => (
            <p key={sentence} className="mt-1 first:mt-0">
              {sentence}
            </p>
          ))}
        </div>
      )}

      {/* The backend's own words come through when it has any: a rule reaching
          outside the admin's tree is refused with a sentence saying what to
          change, which "reload the page" would bury. */}
      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
          {refusal ||
            (rows
              ? "This list could not be refreshed, so it may be out of date. Reload the page to try again."
              : "These notifications could not be loaded. Reload the page to try again.")}
        </div>
      )}

      <div className="mt-6">
        <ComplianceProgramTimelineDays
          headline={false}
          rows={automatic}
          empty={
            gap !== null ? (
              // The dates, the steps and the participants are all on other tabs,
              // so the message says which one rather than pointing "above".
              timelineGapMessage(gap, noun.toLowerCase(), { here: false })
            ) : loading ? (
              <Loading size="5" />
            ) : // The banner above already says what went wrong.
            error ? null : (
              `This ${noun.toLowerCase()} sends nothing on its own. Every step it has is one nobody is notified about.`
            )
          }
        />
      </div>

      <div className="mt-12">
        <ComplianceProgramFormReminders
          proposedComplianceProgram={proposedComplianceProgram}
          onUpdateProposedComplianceProgram={onUpdateProposedComplianceProgram}
          canUpdate={canUpdate}
        />
      </div>
    </>
  );
};

export default ProgramFormNotifications;
